import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { StarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import request from "../config/request";

const ReviewPopup = ({
  open,
  onClose,
  userRole,
  bookingId,
  onReviewSubmitted
}) => {
  const [rating, setRating] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please provide a rating");
      return;
    }

    if (!remarks.trim()) {
      toast.error("Please provide remarks");
      return;
    }

    setIsSubmitting(true);

    try {
      let endpoint, payload;
      
      if (userRole === "user") {
        endpoint = `/api/bookings/user/review/${bookingId}`;
        payload = {
          mentorRating: rating,
          mentorRemarks: remarks.trim()
        };
      } else if (userRole === "mentor") {
        endpoint = `/api/bookings/mentor/review/${bookingId}`;
        payload = {
          userRating: rating,
          userRemarks: remarks.trim()
        };
      } else {
        throw new Error("Invalid user role");
      }

      await request.patch(endpoint, payload);
      
      toast.success("Review submitted successfully!");
      onReviewSubmitted?.();
      handleClose();
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(error.response?.data?.message || "Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRating(0);
      setRemarks("");
      setHoveredRating(0);
      onClose();
    }
  };

  const renderStars = () => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const isActive = star <= (hoveredRating || rating);
          return (
            <button
              key={star}
              type="button"
              className="focus:outline-none transition-transform duration-150 hover:scale-110"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              disabled={isSubmitting}
            >
              {isActive ? (
                <StarIconSolid className="h-8 w-8 text-yellow-400" />
              ) : (
                <StarIcon className="h-8 w-8 text-gray-300 hover:text-yellow-400" />
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const getTitle = () => {
    if (userRole === "user") {
      return "Rate Your Mentor";
    } else if (userRole === "mentor") {
      return "Rate Your Mentee";
    }
    return "Rate Your Experience";
  };

  const getPlaceholder = () => {
    if (userRole === "user") {
      return "Share your thoughts about the mentor's guidance, communication, and helpfulness...";
    } else if (userRole === "mentor") {
      return "Share your thoughts about the mentee's engagement, preparation, and interaction...";
    }
    return "Share your thoughts about this session...";
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="relative px-6 pt-6 pb-4">
                  <button
                    type="button"
                    className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                  
                  <div className="text-center">
                    <Dialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                      {getTitle()}
                    </Dialog.Title>
                    <p className="text-sm text-gray-600">
                      Help us improve by sharing your experience
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 pb-6">
                  {/* Rating Section */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Rating *
                    </label>
                    <div className="flex justify-center">
                      {renderStars()}
                    </div>
                    {rating > 0 && (
                      <p className="text-center text-sm text-gray-500 mt-2">
                        {rating} out of 5 stars
                      </p>
                    )}
                  </div>

                  {/* Remarks Section */}
                  <div className="mb-6">
                    <label htmlFor="remarks" className="block text-sm font-medium text-gray-700 mb-2">
                      Comments *
                    </label>
                    <textarea
                      id="remarks"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder={getPlaceholder()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none transition-colors"
                      rows={4}
                      disabled={isSubmitting}
                      maxLength={500}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-500">
                        Share your honest feedback
                      </p>
                      <p className="text-xs text-gray-400">
                        {remarks.length}/500
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500 shadow-sm"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Skip for now
                  </button>
                  
                  <button
                    type="button"
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white bg-violet-600 text-white hover:bg-violet-700 focus:ring-violet-500 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSubmit}
                    disabled={isSubmitting || rating === 0 || !remarks.trim()}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Submit Review"
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ReviewPopup;